import { log } from "../../../";

import { findDOMNode } from "react-dom";
import * as React from "react";
import { observable, action } from "mobx";
import { observer } from "mobx-react";

// Stores
import { RootStore } from "stores";

// Models
import * as CustomLists from "../../../models/customLists";

// Material UI Components
import Tooltip from "@material-ui/core/Tooltip";
import Tab from "@material-ui/core/Tab";
import Tabs from "@material-ui/core/Tabs";
import AppBar from "@material-ui/core/AppBar";
import Fab from "@material-ui/core/Fab";

import LinearProgress from "@material-ui/core/LinearProgress";

// Components
import { Colors } from "../../../core/utils/Colors";
import { SearchBox } from "../../../components/SearchBox";
import FetchList from "./FetchList";
import { LanguageFilters } from "components/LanguageFilters";
import { BrowseListsViewDialogs } from "./BrowseListsViewDialogs";
import * as Icons from "../../../core/utils/Icons";
import Breadcrumbs from "../../Breadcrumbs";

// Utils
import { WindowResizeChangeDispatcher } from "../../../core/utils/WindowResizeChangeDispatcher";
import * as DeviceSize from "core/utils/DeviceSize";
import { localizedLanguages } from "models";

// Localization
import { strings } from "../../../core/localizedStrings";

import { Row, Col } from "layout";
import { Dialog } from "@material-ui/core";
import { CategoryEditorDetails } from "../createListsView/CategoryEditorDetails";

require("../../../styles/commonStyles.css");

export interface BrowseListsViewProps extends React.Props<any> {
    rootStore: RootStore;
}

const BOTTOM_PADDING: number = 5;

@observer
export class BrowseListsView extends React.Component<BrowseListsViewProps> {
    @observable
    private listContainerHeight: number = 600;
    @observable
    private listHeight: number = 500;

    componentDidMount() {
        this.props.rootStore.browseListsStore.handleTabChange(this.props.rootStore.browseListsStore.currentTab);
        WindowResizeChangeDispatcher.getInstance().addListener(this.onWindowResize);
    }

    componentWillUnmount() {
        WindowResizeChangeDispatcher.getInstance().removeListener(this.onWindowResize);
    }

    public render() {
        if (this.props.rootStore.browseListsStore === undefined || !this.props.rootStore.browseListsStore.isHydrated) {
            return null;
        }

        return (
            <div className="full-width" style={{ paddingTop: 55 }}>
                <Row>
                    <Col xs={12}>
                        <DeviceSize.Tablet>{this.renderMobileTabs()}</DeviceSize.Tablet>
                        <DeviceSize.Desktop>{this.renderTabs()}</DeviceSize.Desktop>
                    </Col>
                </Row>
                <Row>
                    <Col xs={12}>
                        <div id="listViewContainer" style={{ maxHeight: this.listContainerHeight }}>
                            {this.renderListView()}
                        </div>
                    </Col>
                </Row>

                <BrowseListsViewDialogs store={this.props.rootStore.browseListsStore} />
                <Dialog
                    fullWidth={true}
                    open={this.props.rootStore.browseListsStore.shouldShowCategoryDetailsEditor}
                    onClose={this.onEditDetailsClose}
                >
                    <CategoryEditorDetails
                        category={this.props.rootStore.browseListsStore.categoryToCreate}
                        store={this.props.rootStore.browseListsStore}
                    />
                </Dialog>
            </div>
        );
    }

    // Edit cateogry details
    private onEditDetailsClose = () => {
        this.props.rootStore.browseListsStore.shouldShowCategoryDetailsEditor = false;
    };

    @action
    private onWindowResize = (windowWidth: number, windowHeight: number) => {
        let domNode = findDOMNode(document.getElementById("list")) as Element;
        if (domNode) {
            var listBoundingRect = domNode.getBoundingClientRect();

            var listContainerNode = findDOMNode(document.getElementById("listViewContainer")) as Element;
            var listContainerBoundingRect = listContainerNode.getBoundingClientRect();

            this.listHeight = windowHeight - listBoundingRect.top - BOTTOM_PADDING;
            this.listContainerHeight = windowHeight - listContainerBoundingRect.top - BOTTOM_PADDING;
        }
    };

    private handleTabChange = (event: any, value: any) => {
        this.props.rootStore.browseListsStore.handleTabChange(value);
    };

    private handleScroll = (event: any) => {
        let node = event.target;

        let top = node.scrollTop;
        let height = node.scrollHeight;
        let offset = node.offsetHeight;

        if (top > height - offset - 1) {
            this.props.rootStore.browseListsStore.checkShouldFetchMore();
        }

        if (top === 0) {
            log.verbose("top");
        }
    };

    private onSearchTextChange = (text: string) => {
        this.props.rootStore.browseListsStore.search(text);
    };

    @action
    private onCreateListsButtonClicked = async () => {
        let { browseListsStore } = this.props.rootStore;
        if (browseListsStore.currentTab !== CustomLists.TabSelection.MY_LISTS) {
            browseListsStore.handleTabChange(CustomLists.TabSelection.MY_LISTS);
        }
        if (!browseListsStore.rootStore.authStore.isSignedIn) {
            browseListsStore.rootStore.showLoginRequiredMessage(strings.loginToCreateLists);
        } else {
            browseListsStore.setShouldShowCategoryListSelector(true);
        }
    };

    private onAddFolderButtonClicked = (event: any) => {
        let { browseListsStore } = this.props.rootStore;
        if (
            browseListsStore.currentTab !== CustomLists.TabSelection.MY_LISTS &&
            browseListsStore.currentTab !== CustomLists.TabSelection.FAVORITES
        ) {
            browseListsStore.handleTabChange(CustomLists.TabSelection.MY_LISTS);
        }

        if (!this.props.rootStore.browseListsStore.rootStore.authStore.isSignedIn) {
            this.props.rootStore.browseListsStore.rootStore.showLoginRequiredMessage(strings.loginToCreateFolders);
        } else {
            this.props.rootStore.browseListsStore.setFolderTitleToEdit(new CustomLists.Folder());
        }
    };

    private onRefreshButtonClicked = (event: any) => {
        this.props.rootStore.browseListsStore.refreshCurrentFolder();
    };

    private renderTabs() {
        let siteLabel = strings.siteLists;
        let publicLabel = strings.publicLists;
        let myListsLabel = strings.myLists;
        let favoritesLabel = strings.favorites;

        if (this.props.rootStore.browseListsStore.isSearching) {
            let siteCount = this.props.rootStore.browseListsStore.siteListsSearchCount;
            let publicCount = this.props.rootStore.browseListsStore.publicListsSearchCount;
            let myListsCount = this.props.rootStore.browseListsStore.myListsSearchCount;
            let favoritesCount = this.props.rootStore.browseListsStore.favoriteListsSearchCount;

            if (siteCount > 0) {
                siteLabel += " (" + siteCount + ")";
            }

            if (publicCount > 0) {
                publicLabel += " (" + publicCount + ")";
            }

            if (myListsCount > 0) {
                myListsLabel += " (" + myListsCount + ")";
            }

            if (favoritesCount > 0) {
                favoritesLabel += " (" + favoritesCount + ")";
            }
        }

        // let hasFavorites = false;
        // let favoritesFolder = this.props.rootStore.browseListsStore.folderTabs.get(CustomLists.TabSelection.FAVORITES);
        // if (favoritesFolder) {
        //     hasFavorites = favoritesFolder.visibleItems.length > 0;
        // }

        let tabStyle = { margin: "-7px 0 -10px 0" };
        return (
            <AppBar position="static" color="default">
                <Tabs
                    onChange={this.handleTabChange}
                    value={this.props.rootStore.browseListsStore.currentTab}
                    indicatorColor="secondary"
                    textColor="secondary"
                    centered={true}
                    variant="fullWidth"
                >
                    <Tab
                        style={tabStyle}
                        label={siteLabel}
                        value={CustomLists.TabSelection.SITE}
                        icon={<img style={{ height: 20 }} src={require(`assets/images/icon.png`)} />}
                    />
                    <Tab
                        style={tabStyle}
                        label={myListsLabel}
                        value={CustomLists.TabSelection.MY_LISTS}
                        icon={<Icons.MyLists />}
                    />
                    <Tab
                        style={tabStyle}
                        label={publicLabel}
                        value={CustomLists.TabSelection.PUBLIC}
                        icon={<Icons.PublicLists />}
                    />
                    <Tab
                        style={tabStyle}
                        label={favoritesLabel}
                        value={CustomLists.TabSelection.FAVORITES}
                        icon={<Icons.Favorite />}
                    />
                </Tabs>
            </AppBar>
        );
    }

    private renderMobileTabs() {
        return (
            <AppBar position="static" color="default">
                <Tabs
                    onChange={this.handleTabChange}
                    value={this.props.rootStore.browseListsStore.currentTab}
                    indicatorColor="secondary"
                    textColor="secondary"
                    centered={true}
                >
                    <Tab
                        icon={<img style={{ height: 20 }} src={require(`assets/images/icon.png`)} />}
                        value={CustomLists.TabSelection.SITE}
                    />
                    <Tab icon={<Icons.MyLists />} value={CustomLists.TabSelection.MY_LISTS} />
                    <Tab icon={<Icons.PublicLists />} value={CustomLists.TabSelection.PUBLIC} />
                    <Tab icon={<Icons.Favorite />} value={CustomLists.TabSelection.FAVORITES} />
                </Tabs>
            </AppBar>
        );
    }

    private renderListView() {
        let iconStyle = { width: 40, height: 40, marginRight: 3 };

        let tab = this.props.rootStore.browseListsStore.currentTab;
        let isSiteListAndAdmin =
            (tab === CustomLists.TabSelection.SITE || tab === CustomLists.TabSelection.SITE_IMAGES) &&
            this.props.rootStore.browseListsStore.rootStore.userStore.user.isAdmin;
        let shouldShowCreateNewListButton = tab === CustomLists.TabSelection.MY_LISTS || isSiteListAndAdmin;
        let shouldShowAddFolderButton =
            (tab === CustomLists.TabSelection.MY_LISTS ||
                tab === CustomLists.TabSelection.FAVORITES ||
                isSiteListAndAdmin) &&
            !this.props.rootStore.browseListsStore.isSearching;
        let shouldShowRefreshButton = tab === CustomLists.TabSelection.PUBLIC;

        return (
            <div>
                {/* <Row center="xs">
                    <BrowseListsToolbar store={this.props.rootStore.browseListsStore} />
                </Row> */}
                <Row style={{ backgroundColor: Colors.rgba(255, 255, 255, 0.7) }}>
                    <Col xs={6}>
                        <Row start="xs">
                            <LanguageFilters
                                currentLanguage={this.props.rootStore.browseListsStore.languageFilter}
                                onLanguageChangeCallback={this.handleLanguageChange}
                                languages={localizedLanguages}
                            />
                        </Row>
                    </Col>
                    <Col xs={6} style={{ paddingTop: 5 }}>
                        <Row end="xs" style={{ paddingRight: 15 }}>
                            {shouldShowCreateNewListButton ? (
                                <Tooltip title={strings.createNewList} placement="bottom">
                                    <Fab
                                        disabled={this.props.rootStore.browseListsStore.shouldShowCategoryListSelector}
                                        color="primary"
                                        onClick={this.onCreateListsButtonClicked}
                                        style={iconStyle}
                                    >
                                        <Icons.AddList color="secondary" />
                                    </Fab>
                                </Tooltip>
                            ) : null}
                            {shouldShowAddFolderButton ? (
                                <Tooltip title={strings.addFolder} placement="bottom">
                                    <Fab
                                        disabled={this.props.rootStore.browseListsStore.folderToEdit !== undefined}
                                        color="primary"
                                        onClick={this.onAddFolderButtonClicked}
                                        style={iconStyle}
                                    >
                                        <Icons.AddFolder color="secondary" />
                                    </Fab>
                                </Tooltip>
                            ) : null}
                            {/* {shouldShowRefreshButton ? (
                                <Tooltip title={strings.refresh} placement="bottom">
                                    <Fab color="primary" onClick={this.onRefreshButtonClicked} style={iconStyle}>
                                        <Icons.Replay color="secondary" />
                                    </Fab>
                                </Tooltip>
                            ) : null} */}
                        </Row>
                    </Col>
                </Row>
                <Row middle="xs" style={{ marginBottom: -10, paddingRight: 20 }}>
                    <Col>
                        <Row style={{ padding: "10px 10px 15px 10px" }}>
                            {this.props.rootStore.browseListsStore.isSearching ? (
                                <div style={{ height: 10 }} />
                            ) : (
                                <Breadcrumbs store={this.props.rootStore.browseListsStore} />
                            )}
                        </Row>
                    </Col>
                </Row>
                <Row style={{ paddingLeft: 10, paddingRight: 10, paddingBottom: 10 }}>
                    <Col xs={12}>
                        <SearchBox
                            hasBackground={false}
                            disableUnderline={false}
                            fontSize={22}
                            fontColor={Colors.white}
                            showLoading={this.props.rootStore.browseListsStore.isSearchQuerying}
                            onSearchTextChange={this.onSearchTextChange}
                        />
                    </Col>
                </Row>

                {this.props.rootStore.browseListsStore.isLoading &&
                    this.props.rootStore.browseListsStore.visibleItems.length === 0 && (
                        <LinearProgress color="primary" />
                    )}
                <Row>
                    <div
                        onScroll={this.handleScroll}
                        id="list"
                        style={{
                            overflowY: "auto",
                            overflowX: "hidden",
                            width: "98%",
                            maxHeight: this.listHeight - 10,
                            paddingLeft: 10,
                            paddingRight: 10,
                        }}
                    >
                        <FetchList store={this.props.rootStore.browseListsStore} />
                    </div>
                </Row>
                {this.props.rootStore.browseListsStore.isLoading &&
                    this.props.rootStore.browseListsStore.visibleItems.length > 0 && <LinearProgress color="primary" />}
            </div>
        );
    }

    private handleLanguageChange = (event: any) => {
        let value = event.target.value;
        if (value === -1) {
            this.props.rootStore.browseListsStore.setLanguageFilter(undefined);
        } else {
            this.props.rootStore.browseListsStore.setLanguageFilter(value);
        }
    };
}
